import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, X, ChevronLeft, ChevronRight } from "lucide-react";
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, format, addMonths, isSameMonth, isSameDay } from "date-fns";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function Planner() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [month, setMonth] = useState(new Date());

  const load = () => api.get("/mealplan").then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  const remove = async (item_id) => {
    await api.delete(`/mealplan/item/${item_id}`);
    setItems((it) => it.filter((x) => x.item_id !== item_id));
    toast.success("Removed from plan");
  };
  const clearAll = async () => { await api.post("/mealplan/clear"); setItems([]); toast.success("Planner cleared"); };

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [month]);

  const byDate = useMemo(() => {
    const map = {};
    items.forEach((i) => { (map[i.day] = map[i.day] || []).push(i); });
    return map;
  }, [items]);

  const today = new Date();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8 animate-fade-up">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Meal Planner</h1>
        <div className="flex items-center gap-2">
          <button data-testid="prev-month-btn" onClick={() => setMonth((m) => addMonths(m, -1))}
            className="w-10 h-10 rounded-full bg-nerd-card border border-black/10 flex items-center justify-center hover:bg-black/5 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
          <span data-testid="planner-month-label" className="min-w-[150px] text-center font-display font-bold text-lg">{format(month, "MMMM yyyy")}</span>
          <button data-testid="next-month-btn" onClick={() => setMonth((m) => addMonths(m, 1))}
            className="w-10 h-10 rounded-full bg-nerd-card border border-black/10 flex items-center justify-center hover:bg-black/5 transition-colors"><ChevronRight className="w-4 h-4" /></button>
          {items.length > 0 && (
            <button data-testid="clear-plan-btn" onClick={clearAll}
              className="ml-2 inline-flex items-center gap-2 bg-nerd-card border border-black/10 hover:border-black/25 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors"><Trash2 className="w-4 h-4" /> Clear</button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-7 gap-px bg-black/10 rounded-2xl overflow-hidden border border-black/10">
        {WD.map((w) => (
          <div key={w} className="bg-nerd-card py-2 text-center text-[11px] font-mono-x uppercase tracking-widest text-nerd-light/40">{w}</div>
        ))}
        {days.map((d) => {
          const key = iso(d);
          const dayItems = byDate[key] || [];
          const inMonth = isSameMonth(d, month);
          const isToday = isSameDay(d, today);
          return (
            <div key={key} data-testid={`day-cell-${key}`} className={`bg-nerd-bg min-h-[104px] p-1.5 ${inMonth ? "" : "opacity-40"}`}>
              <div className={`text-xs font-semibold mb-1 w-6 h-6 flex items-center justify-center rounded-full ${isToday ? "bg-nerd-light text-nerd-bg" : "text-nerd-light/70"}`}>{format(d, "d")}</div>
              <div className="space-y-1">
                {dayItems.map((it) => (
                  <div key={it.item_id} onClick={() => navigate(`/recipe/${it.recipe_id}`)}
                    className="group relative rounded-lg overflow-hidden bg-nerd-depth cursor-pointer">
                    <img src={it.thumbnail} alt={it.title} className="w-full h-12 object-cover opacity-90" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent p-1 flex items-end">
                      <p className="text-[9px] font-semibold text-white leading-tight line-clamp-2">{it.title}</p>
                    </div>
                    <button data-testid={`remove-item-${it.item_id}`} onClick={(e) => { e.stopPropagation(); remove(it.item_id); }}
                      className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/50 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-coral"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
